import React, { useState } from 'react';
import Icon, { Size } from 'components/general/icon/icon';
import Chip from 'components/general/chip/Chip';
import OfficeLocationsTooltip from 'components/office_locations_tooltip/office_locations_tooltip';

interface Skill {
  id: number;
  name: string;
}

const JobDetails = ({ jobAd }) => {
  const styles = require('./JobDetails.module.scss');
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleDetails = () => setIsExpanded(!isExpanded);

  const renderSalary = () => {
    if (!jobAd.salary && !jobAd.rate) return 'Gross annual: Not specified';

    return (
      <div className={styles.salary}>
        {jobAd.salary && <span>Gross annual: {jobAd.salary}</span>}
        {jobAd.rate && <span>Rate per day: {jobAd.rate}</span>}
      </div>
    );
  };

  const renderLanguages = () => {
    const preferred = (jobAd.preferred_languages_labels || []).join(', ');
    const niceToHave = jobAd.nice_to_have_language_label;

    if (!preferred && !niceToHave) {
      return 'Languages: Not specified';
    }

    const languages = [];

    if (preferred) {
      languages.push(`${preferred} (required)`);
    }

    if (niceToHave) {
      languages.push(`${niceToHave} (nice to have)`);
    }

    return languages.join(', ');
  };

  const formatExp = (experience: number) => {
    return experience === 10 ? '10+' : experience;
  };

  const experienceRange = () => {
    if (jobAd.experience_min && jobAd.experience_max) {
      return `${jobAd.experience_min} - ${formatExp(jobAd.experience_max)} years of experience -`;
    } else if (jobAd.experience_min) {
      return `Min ${formatExp(jobAd.experience_min)} ${
        jobAd.experience_min === 1 ? 'year of experience -' : 'years of experience -'
      }`;
    } else if (jobAd.experience_max) {
      return `Max ${formatExp(jobAd.experience_max)} ${
        jobAd.experience_max === 1 ? 'year of experience -' : 'years of experience -'
      }`;
    } else {
      return 'Years of experience not specified';
    }
  };

  const offerLocation = () => {
    if (!jobAd.location) {
      return 'Location not specified';
    }

    return (
      <div className={jobAd.office_locations?.length > 0 ? styles.locationWithTooltip : ''}>
        {jobAd.location}
        {jobAd.remote_working_label && ` - ${jobAd.remote_working_label}`}
        {jobAd.office_locations?.length > 1 && (
          <OfficeLocationsTooltip
            officeLocations={jobAd.office_locations}
            iconColor="tuftsBlueBase"
            iconSize={Size.Small}
          />
        )}
      </div>
    );
  };

  const renderToggleButton = () => (
    <button
      className={styles.jobDetailsToggle}
      onClick={toggleDetails}
      type="button"
    >
      <span className={styles.toggleText}>
        {isExpanded ? 'Hide job detail' : 'Show job detail'}
      </span>
      <span className={styles.toggleIcon}>
        {isExpanded ? (
          <Icon name="chevronUp" size={Size.Big} color={'tuftsBlueMidDark'} />
        ) : (
          <Icon name="chevronDown" size={Size.Big} color={'tuftsBlueMidDark'} />
        )}
      </span>
    </button>
  );

  return (
    <div className={styles.jobDetailsCard}>
      <div className={styles.jobDetailsCardContent}>
        {!isExpanded && renderToggleButton()}

        {isExpanded && (
          <div className={styles.jobDetailsBody}>
            <div className={styles.jobInfoGrid}>
              <div className={styles.jobInfoColumns}>
                <div className={styles.jobInfoItem}>
                  <span className={styles.infoIcon}><Icon name="assignment" color={'blackBase'} /></span>
                  <span className={styles.infoText}>{jobAd.job_type || 'Not specified'} {jobAd.project_duration && '- ' + jobAd.project_duration + ' months'}</span>
                </div>
                <div className={styles.jobInfoItem}>
                  <span className={styles.infoIcon}><Icon name='payment' color={'blackBase'} /></span>
                  <span className={styles.infoText}>{renderSalary()}</span>
                </div>
                <div className={styles.jobInfoItem}>
                  <span className={styles.infoIcon}><Icon name="locationDetail" color={'blackBase'}/></span>
                  <span className={styles.infoText}>
                    {offerLocation()}
                  </span>
                </div>
              </div>

              <div className={styles.jobInfoColumns}>
                <div className={styles.jobInfoItem}>
                  <span className={styles.infoIcon}><Icon name="signal" color={'blackBase'} /></span>
                  <span className={styles.infoText}>{experienceRange()} {jobAd.experience_label}</span>
                </div>
                {jobAd.citizenship && (
                  <div className={styles.jobInfoItem}>
                    <span className={styles.infoIcon}><Icon name="card" color={'blackBase'} /></span>
                    <span className={styles.infoText}> Requires work permit </span>
                  </div>
                )}
                <div className={styles.jobInfoItem}>
                  <span className={styles.infoIcon}><Icon name="chat" color={'blackBase'} /></span>
                  <span className={styles.infoText}>
                    {renderLanguages()}
                  </span>
                </div>
              </div>
            </div>

            {(jobAd.must_have_skills?.length > 0 || jobAd.other_required_skills?.length > 0) && (
              <div className={styles.skillsGroup}>
                <div className={styles.skillsLabel}>Must have skills:</div>
                <div className={styles.skillsList}>
                  {[...(jobAd.must_have_skills || []), ...(jobAd.other_required_skills || [])].map((skill: Skill) => (
                    <Chip key={"skill" + skill.id} text={skill.name}></Chip>
                  ))}
                </div>
              </div>
            )}

            {jobAd.nice_to_have_skills?.length > 0 && (
              <div className={styles.skillsGroup}>
                <div className={styles.skillsLabel}>Nice to have skills:</div>
                <div className={styles.skillsList}>
                  {jobAd.nice_to_have_skills.map((skill: Skill) => (
                    <Chip key={"skill" + skill.id} text={skill.name}></Chip>
                  ))}
                </div>
              </div>
            )}
            
            {renderToggleButton()}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobDetails;
